/**
 * Analytics Event Tracking Utilities
 * Observability & Analytics Infrastructure - Task 4.4
 * Feedback Context Enhancement - Task Group 1
 *
 * Wrapper functions for core product events.
 * Auto-attaches user ID, feature flags, and timestamp.
 * Applies PII scrubbing to all properties.
 *
 * Event Categories:
 * - Navigation: $pageview
 * - Auth: login, logout, registration
 * - Upload: upload_started, upload_success, upload_failed
 * - Extraction: extraction_started, extraction_success, extraction_failed
 * - Billing: plan_viewed, checkout_started, portal_opened
 * - Feedback: feedback_submitted
 *
 * (Removed 2026-08-29: trackAccountSetupStarted/Completed,
 *  trackPreferencesSaved, trackReportGenerated/Downloaded/Exported,
 *  trackPaymentSuccess/Failed — zero production callers. Backend already
 *  emits payment_succeeded/payment_failed server-side; the remaining
 *  funnel/report tracking needs design sign-off before promoting to
 *  call sites. computeAuditClaimSummary, detectFeatureUsage,
 *  computeProcessingTimeBucket, and trackFeedbackSubmitted bodies
 *  preserved byte-identical to pre-hardening.)
 */

import { trackEvent } from "./posthog";
import { hashText } from "@vardic/shared/utils/pii-scrubbing";
import { summarizeClaimSections } from "@vardic/shared/utils/claim-context-grouping";
import type { AuditWithEvidence } from "@/types";

type AuditWithOptionalProcessingTime = AuditWithEvidence & {
  processingTimeMs?: unknown;
};

// Navigation Events

/**
 * Manual $pageview. `capture_pageview` is off in posthog.ts (manual
 * tracking only), so nothing fires this automatically. `path` should
 * already be route-normalized by the caller (e.g. "/results/:auditId", not
 * the raw audit ID) — see normalizeAnalyticsPath in App.tsx.
 */
export async function trackPageview(path: string): Promise<void> {
  await trackEvent("$pageview", { path });
}

// Authentication Events

export async function trackLogin(): Promise<void> {
  await trackEvent("login", {});
}

export async function trackLogout(): Promise<void> {
  await trackEvent("logout", {});
}

// Only defined when the sign-up came from a campaign or self-reported source
// (GTM plan F1/F2); otherwise the event keeps its original bare shape.
export async function trackRegistration(
  properties?: Record<string, unknown>,
): Promise<void> {
  await trackEvent("registration", properties ?? {});
}

// Upload Events

export async function trackUploadStarted(): Promise<void> {
  await trackEvent("upload_started", {});
}

export async function trackUploadSuccess(
  fileSize: number,
  fileExt: string,
): Promise<void> {
  await trackEvent("upload_success", {
    fileSize,
    fileExt,
  });
}

export async function trackUploadFailed(error: string): Promise<void> {
  await trackEvent("upload_failed", {
    error,
  });
}

// Extraction Events

export async function trackExtractionStarted(): Promise<void> {
  await trackEvent("extraction_started", {});
}

export async function trackExtractionSuccess(
  claimCount: number,
): Promise<void> {
  await trackEvent("extraction_success", {
    claimCount,
  });
}

export async function trackExtractionFailed(error: string): Promise<void> {
  await trackEvent("extraction_failed", {
    error,
  });
}

// Billing Events

export async function trackPlanViewed(): Promise<void> {
  await trackEvent("plan_viewed", {});
}

export async function trackCheckoutStarted(plan: string): Promise<void> {
  await trackEvent("checkout_started", {
    plan,
  });
}

export async function trackPortalOpened(): Promise<void> {
  await trackEvent("portal_opened", {});
}

// Feedback Events

/**
 * Compute processing time bucket for categorization
 * Fast: < 3000ms, Normal: 3000-10000ms, Slow: > 10000ms
 */
function computeProcessingTimeBucket(ms: number): "fast" | "normal" | "slow" {
  if (ms < 3000) return "fast";
  if (ms <= 10000) return "normal";
  return "slow";
}

/**
 * Risk breakdown + total from the audit's *visible* claim cards.
 *
 * pirx-ih09z: previously this counted every DB row, double-counting
 * atomic claims absorbed into a `claim_context_grouping` merged row
 * (the same shape as `ResultsSummaryHeader.tsx:65-69`). Now derived
 * via the shared helper so the analytics event agrees with the
 * header / nav-toggle label / PDF export / ClaimsPanel card count.
 */
function computeAuditClaimSummary(audit: AuditWithEvidence): {
  claimCount: number;
  riskBreakdown: { red: number; yellow: number; green: number };
} {
  const summary = summarizeClaimSections(audit.claims);
  return {
    claimCount: summary.total,
    riskBreakdown: {
      red: summary.byLevel.high,
      yellow: summary.byLevel.medium,
      green: summary.byLevel.low,
    },
  };
}

/**
 * Detect feature usage from audit data presence
 * Returns feature flags and counts based on actual data, not feature flags
 */
function detectFeatureUsage(audit: AuditWithEvidence) {
  // Evidence documents
  const hasEvidenceDocuments = (audit.evidenceDocuments?.length ?? 0) > 0;
  const evidenceDocCount = audit.evidenceDocuments?.length ?? 0;

  // Evidence matching - check if any claims have evidenceMatches
  let matchCount = 0;
  for (const claim of audit.claims) {
    matchCount += claim.evidenceMatches?.length ?? 0;
  }
  const hasEvidenceMatching = matchCount > 0;

  // Gap detection - check if any claims have evidenceGaps
  let gapCount = 0;
  for (const claim of audit.claims) {
    gapCount += claim.evidenceGaps?.length ?? 0;
  }
  const hasGapDetection = gapCount > 0;

  return {
    hasEvidenceDocuments,
    evidenceDocCount,
    hasEvidenceMatching,
    matchCount,
    hasGapDetection,
    gapCount,
  };
}

/**
 * Track feedback submission with enriched audit metadata
 *
 * Automatically captures 20+ audit properties for PostHog correlation analysis:
 * - Audit identification (auditId, documentNameHash)
 * - Audit outcomes (claimCount, overallRiskLevel, riskBreakdown)
 * - Performance (processingTimeMs, processingTimeBucket)
 * - Feature usage (hasEvidenceDocuments, evidenceDocCount, hasEvidenceMatching, matchCount, etc.)
 * - User feedback (rating, commentLength)
 *
 * PII Protection:
 * - Document name is hashed (SHA-256) before sending
 * - Comment text is omitted (only length is sent)
 * - Missing fields are omitted (no null values)
 *
 * @param auditId Audit ID
 * @param rating User rating (1-5 stars)
 * @param comment User comment text (stored in DB, NOT sent to PostHog)
 * @param audit Audit object with evidence data
 */
export async function trackFeedbackSubmitted(
  auditId: string,
  rating: number,
  comment: string,
  audit: AuditWithEvidence,
): Promise<void> {
  try {
    // Hash document name for PII protection
    const documentNameHash = await hashText(audit.documentName);

    // Compute audit metadata (pirx-ih09z: section-derived via shared helper)
    const { claimCount, riskBreakdown } = computeAuditClaimSummary(audit);
    const overallRiskLevel = audit.overallRiskLevel;
    // Detect feature usage from data presence
    const featureUsage = detectFeatureUsage(audit);

    // Build event properties with conditional spreading (omit missing fields)
    const properties: Record<string, unknown> = {
      auditId,
      documentNameHash,
      rating,
      claimCount,
      overallRiskLevel,
      riskBreakdown,
      ...featureUsage,
    };

    // Add comment length if comment is not empty
    const trimmedComment = comment.trim();
    if (trimmedComment.length > 0) {
      properties.commentLength = trimmedComment.length;
    }

    // Add processing time metrics if available
    const processingTimeMs = (audit as AuditWithOptionalProcessingTime)
      .processingTimeMs;
    if (typeof processingTimeMs === "number") {
      properties.processingTimeMs = processingTimeMs;
      properties.processingTimeBucket = computeProcessingTimeBucket(
        processingTimeMs,
      );
    }

    // Add evidence sufficiency score if available
    if (typeof audit.evidenceSufficiencyScore === "number") {
      properties.evidenceSufficiencyScore = audit.evidenceSufficiencyScore;
    }

    // Track event (trackEvent auto-injects featureFlags and timestamp)
    await trackEvent("feedback_submitted", properties);
  } catch (error) {
    // Non-blocking: log error but don't crash
    console.error("[Analytics] Failed to track feedback submission:", error);
  }
}